import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const differentiators = [
  { icon: '🏢', title: 'Founders & Operators', desc: 'We understand the pressures of running a business from the inside - P&L, hiring, customers, and cash flow.' },
  { icon: '🔗', title: 'Systems Integration Experts', desc: 'Decades of experience connecting tools, managing complexity, and delivering results across complex tech stacks.' },
  { icon: '💼', title: 'SaaS GM Experience', desc: 'We speak the language of business owners, GMs, and COOs - not just technologists.' },
  { icon: '🤖', title: 'Actual AI Implementation', desc: 'We\'ve built Gen AI and ML systems in production - not just theoretical or advisory experience.' },
];

export default function About() {
  return (
    <>
      <Helmet>
        <title>About Us | Sagient Partners AI Consulting</title>
        <meta name="description" content="Sagient Partners is founded by operators and systems integration experts who have built AI in production. We bring hands-on experience - not just advice - to your business." />
        <link rel="canonical" href="https://sagientpartners.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sagientpartners.com/about" />
        <meta property="og:title" content="About Us | Sagient Partners AI Consulting" />
        <meta property="og:description" content="Sagient Partners is founded by operators and systems integration experts who have built AI in production. We bring hands-on experience - not just advice - to your business." />
        <meta property="og:image" content="https://sagientpartners.com/social_card.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="About Us | Sagient Partners AI Consulting" />
        <meta name="twitter:description" content="Sagient Partners is founded by operators and systems integration experts who have built AI in production. We bring hands-on experience - not just advice - to your business." />
        <meta name="twitter:image" content="https://sagientpartners.com/social_card.png" />
      </Helmet>
      {/* Header */}
      <section style={{ background: 'linear-gradient(135deg, #0C447C 0%, #0A1628 100%)', padding: '40px 0 48px', color: '#fff' }}>
        <div className="container">
          <p className="section-label" style={{ color: '#B5D4F4' }}>About Us</p>
          <h1 style={{ color: '#fff', maxWidth: 620, marginBottom: 20 }}>Founders Who've Done It. Advisors Who Get It.</h1>
          <p style={{ color: '#B5D4F4', fontSize: '1.1rem', maxWidth: 600 }}>
            Sagient Partners was founded by two technology veterans with a combined 75+ years of experience building, scaling, and selling businesses - and implementing AI in production.
          </p>
        </div>
      </section>

      {/* Company story */}
      <section className="section">
        <div className="container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div>
            <p className="section-label">Our Story</p>
            <h2 className="section-heading">Why We Started Sagient Partners</h2>
            <p style={{ marginBottom: 20, fontSize: '0.97rem' }}>
              The name "Sagient" is a portmanteau of <em>sage</em> - wisdom and strategic thinking - and <em>agent</em> - the AI agents that take autonomous action on your behalf. That duality is exactly what we deliver.
            </p>
            <p style={{ marginBottom: 20, fontSize: '0.97rem' }}>
              We saw a clear gap: most nonprofits and small businesses know AI is important but don't have the internal expertise to navigate it strategically. Large consultancies are too expensive and too abstract. Freelancers can build tools but can't set strategy.
            </p>
            <p style={{ fontSize: '0.97rem' }}>
              Sagient Partners sits in the sweet spot - experienced operators who can think like a business owner <em>and</em> build like an engineer.
            </p>
          </div>
          <div style={{ background: '#EBF4FD', borderRadius: 14, padding: 40 }}>
            <p style={{ fontWeight: 700, color: '#0C447C', fontSize: '1.1rem', marginBottom: 20 }}>The Name & Logo</p>
            <p style={{ fontSize: '0.93rem', marginBottom: 16 }}>
              The Sagient Partners icon combines two ideas: the "S" of Sagient, and two nodes connected by a neural path - visually communicating AI agents working together.
            </p>
            <p style={{ fontSize: '0.93rem' }}>
              Simple enough to work at small sizes. Distinctive enough to be memorable. The dual-read - letterform and diagram - maps directly to the agentic consulting pitch: the logo <em>is</em> the product concept.
            </p>
            <div style={{ marginTop: 24, padding: '20px 24px', background: '#fff', borderRadius: 10, borderLeft: '4px solid #378ADD' }}>
              <p style={{ fontSize: '0.88rem', color: '#378ADD', fontWeight: 600, marginBottom: 4 }}>Location</p>
              <p style={{ fontSize: '0.93rem' }}>San Francisco Bay Area</p>
            </div>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="section section--alt">
        <div className="container">
          <p className="section-label">The Team</p>
          <h2 className="section-heading" style={{ marginBottom: 48 }}>Founder Profiles</h2>

          {/* John */}
          <div className="card" style={{ marginBottom: 40 }}>
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: 'linear-gradient(135deg, #0C447C, #378ADD)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: '1.8rem', flexShrink: 0,
              }}>JR</div>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: '1.5rem', marginBottom: 4 }}>John Rees</h2>
                <p style={{ color: '#378ADD', fontWeight: 600, marginBottom: 16 }}>Co-Founder</p>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.75 }}>
                  John is a technology executive, two-time founder, and enterprise transformation leader with over 35 years of experience. He and Joe first worked together at KPMG, where John advised Global 2000 clients across financial services, insurance, and technology. The two then co-founded Altro Solutions, building it into a global IT consultancy that grew to $28M in revenue before selling to Burntsand. They went on to co-found Doubleknot, a SaaS platform serving hundreds of nonprofits, museums, and cultural organizations, which was acquired by Togetherwork. Most recently, as VP of Product & Strategy at Togetherwork, John pioneered a Gen AI and ML-powered recommendation engine and drove enterprise-wide AI adoption using LLMs and MCP servers - giving him rare practical credibility as someone who has built AI in production, not just advised on it.
                </p>
              </div>
            </div>
          </div>

          {/* Joe */}
          <div className="card">
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: 'linear-gradient(135deg, #0C447C, #378ADD)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: '1.8rem', flexShrink: 0,
              }}>JG</div>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: '1.5rem', marginBottom: 4 }}>Joseph Garappolo</h2>
                <p style={{ color: '#378ADD', fontWeight: 600, marginBottom: 16 }}>Co-Founder</p>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.75 }}>
                  Joe is a technology executive and entrepreneur with over four decades of experience building and scaling technology businesses. He and John have worked together since KPMG, where Joe served as a Partner advising organizations on complex enterprise challenges. Together they co-founded Altro Solutions, which Joe led as CEO - growing the firm into a global IT consultancy before selling it to Burntsand. The partnership continued with the co-founding of Doubleknot, a SaaS platform that became a leading solution for nonprofits, museums, and cultural organizations, ultimately acquired by Togetherwork. Joe's long track record of building companies alongside John - and successfully exiting both - is a testament to a partnership grounded in trust, complementary skills, and a shared instinct for where technology is headed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="section">
        <div className="container">
          <p className="section-label">Why Sagient Partners</p>
          <h2 className="section-heading" style={{ marginBottom: 40 }}>What Sets Us Apart</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 28 }}>
            {differentiators.map(d => (
              <div key={d.title} style={{ padding: 28, borderRadius: 10, border: '1px solid #EBF4FD' }}>
                <div style={{ fontSize: '1.8rem', marginBottom: 12 }}>{d.icon}</div>
                <h3 style={{ marginBottom: 10 }}>{d.title}</h3>
                <p style={{ fontSize: '0.9rem' }}>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg, #378ADD, #0C447C)', padding: '72px 0', textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ color: '#fff', marginBottom: 16 }}>Work With Us</h2>
          <p style={{ color: '#B5D4F4', fontSize: '1.05rem', marginBottom: 32 }}>
            30-minute discovery call. Free. No obligation. Just a clear-eyed conversation about what AI can do for your business.
          </p>
          <Link to="/contact" className="btn btn-white">Get in Touch →</Link>
        </div>
      </section>
    </>
  );
}
